import React, { useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { Controller } from 'react-hook-form';
import Select from 'react-select';
import { ConvertToInputDate } from '../../../../utils/DateFormat';

const CreateUpdateProductModal = ({ show, handleClose, handleSubmit, onSubmit, errors, productList, control, register, setValue, reset, initialValues = {}, flag, product = {} }) => {

    const statusOptions = [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
    ];
    const productTypeOptions = [
        { value: 'addon', label: 'Add On' },
        { value: 'fresh', label: 'Fresh' },
        { value: 'renewal', label: 'Renewal' },
    ];
    const productOptions = flag === 'renewal'
        ? product ? [{ value: product.value, label: product.label }] : []
        : productList.map(product => ({ value: product.id, label: product.name }));

    // Normal create/edit mode
    useEffect(() => {
        if (!initialValues) {
            reset();
            setValue('product_id', []);
            setValue('product_type', '');
            return;
        }
        if (initialValues?.id) {
            setValue('product_id', initialValues.product_id ? [initialValues.product_id] : []);
            setValue('quantity', initialValues.quantity || '');
            setValue('start_date', ConvertToInputDate(initialValues.start_date) || '');
            setValue('expiry_date', ConvertToInputDate(initialValues.expiry_date) || '');
            setValue('invoice_no', initialValues.invoice_no || '');
            setValue('product_type', initialValues.product_type || '');
            setValue('status', initialValues.status || 'active');
        }
    }, [initialValues, reset, setValue]);

    // Renewal mode only
    useEffect(() => {
        if (flag === 'renewal' && product?.value) {
            setValue('product_id', [product.value]);
            setValue('product_type', flag);
            setValue('status', 'active');
        }
    }, [flag, product?.value, setValue]);


    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
            <Modal.Title>{flag === 'renewal' ? 'Renewal Product' : initialValues?.id ? 'Update Product' : 'Add Product'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form onSubmit={handleSubmit(data => onSubmit(data, initialValues?.id))}>
                    <div className="row mt-3">
                        <div className="col">
                            <label className="fw-bold">Product</label>
                            <Controller
                                name="product_id"
                                control={control}
                                rules={{ required: 'At least one product is required' }}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        options={productOptions}
                                        placeholder="Select products"
                                        isMulti
                                        onChange={(selectedOptions) =>
                                            field.onChange(selectedOptions ? selectedOptions.map(option => option.value) : [])
                                        }
                                        value={productOptions.filter(option => field.value?.includes(option.value))}
                                    />
                                )}
                            />
                            {errors.product_id && <span className="text-danger mt-2">{errors.product_id.message}</span>}
                        </div>
                    </div>
                    <div className="row mt-3">
                        <div className="col-12 col-md-6">
                            <label className="fw-bold">Quantity</label>
                            <input
                                type="number"
                                {...register('quantity', {
                                    required: 'Quantity is required.',
                                    min: { value: 1, message: 'Quantity must be at least 1' }
                                })}
                                className={`form-control ${errors.quantity ? 'is-invalid' : ''}`}
                                placeholder="Enter quantity"
                            />
                            {errors.quantity && <span className="text-danger mt-2">{errors.quantity.message}</span>}
                        </div>
                        <div className="col-12 col-md-6">
                            <label className="fw-bold">Invoice No</label>
                            <input
                                type="text"
                                {...register('invoice_no', {
                                    required: 'Invoice number is required.',
                                })}
                                placeholder='Enter invoice no'
                                className={`form-control ${errors.invoice_no ? 'is-invalid' : ''}`}
                            />
                            {errors.invoice_no && <span className="text-danger mt-2">{errors.invoice_no.message}</span>}
                        </div>
                    </div>
                    <div className="row mt-3">
                        <div className="col-12 col-md-6">
                            <label className="fw-bold">Start Date</label>
                            <input
                                type="date"
                                {...register('start_date', {
                                    required: 'Start date is required.',
                                })}
                                className={`form-control ${errors.start_date ? 'is-invalid' : ''}`}
                            />
                            {errors.start_date && <span className="text-danger mt-2">{errors.start_date.message}</span>}
                        </div>
                        <div className="col-12 col-md-6">
                            <label className="fw-bold">Expiry Date</label>
                            <input
                                type="date"
                                {...register("expiry_date", {
                                    required: "Expiry date is required.",
                                })}
                                className={`form-control ${errors.expiry_date ? 'is-invalid' : ''}`}
                            />
                            {errors.expiry_date && <span className="text-danger mt-2">{errors.expiry_date.message}</span>}
                        </div>
                    </div>
                    <div className="row mt-3">
                        <div className="col-12 col-md-6">
                            <label className="fw-bold">Status</label>
                            <Controller
                                name="status"
                                control={control}
                                rules={{ required: "Status is required" }}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        options={statusOptions}
                                        placeholder="Select status"
                                        isSearchable
                                        classNamePrefix="react-select"
                                        value={statusOptions.find(option => option.value === field.value) || null}
                                        onChange={(selectedOption) => field.onChange(selectedOption?.value)}
                                    />
                                )}
                            />
                            {errors.status && <span className="text-danger mt-2">{errors.status.message}</span>}
                        </div>
                        <div className="col-12 col-md-6">
                            <label className="fw-bold">Product Type</label>
                            {flag === 'renewal' ? (
                                <input
                                    type="text"
                                    {...register('product_type', {
                                        required: 'Product type is required.',
                                    })}
                                    readOnly
                                    className={`form-control ${errors.product_type ? 'is-invalid' : ''}`}
                                />
                            ) : (
                                <Controller
                                    name="product_type"
                                    control={control}
                                    rules={{ required: "Product type is required" }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            options={productTypeOptions}
                                            placeholder="Select product type"
                                            isSearchable
                                            classNamePrefix="react-select"
                                            value={productTypeOptions.find(option => option.value === field.value) || null}
                                            onChange={(selectedOption) => field.onChange(selectedOption?.value)}
                                        />
                                    )}
                                />
                            )}
                            {errors.product_type && <span className="text-danger mt-2">{errors.product_type.message}</span>}
                        </div>
                    </div>
                    <input type="hidden" name="id" value={initialValues?.id || ''} />

                    <div className="modal-footer">
                        <Button variant="danger" size='sm' onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="primary" size='sm'>
                            {initialValues?.id ? 'Save Changes' : 'Save'}
                        </Button>
                    </div>
                </form>
            </Modal.Body>
        </Modal>
    );
};

export default CreateUpdateProductModal;
